import { track } from '@src/services/analytics';
import type { LoginMode } from '@src/features/customers';
import type { PaymentMethodCode } from '@src/domains/payments';

export const CHECKOUT_EVENT = {
  checkoutStarted: 'checkout_started',
  emailSubmitted: 'email_submitted',
  accountFound: 'account_found',
  accountNotFound: 'account_not_found',
  loginPasswordSelected: 'login_password_selected',
  loginCodeSelected: 'login_code_selected',
  codeSent: 'code_sent',
  codeValidated: 'code_validated',
  loginCompleted: 'login_completed',
  accountCreated: 'account_created',
  paymentMethodSelected: 'payment_method_selected',
  pixQrShown: 'pix_qr_shown',
  threeDsShown: 'three_ds_shown',
  paymentFailed: 'payment_failed',
  checkoutCompleted: 'checkout_completed',
} as const;

export type CheckoutEvent = (typeof CHECKOUT_EVENT)[keyof typeof CHECKOUT_EVENT];

export const CHECKOUT_SOURCE = {
  planneLink: 'planne_link',
  site: 'site',
} as const;

export type CheckoutSource = (typeof CHECKOUT_SOURCE)[keyof typeof CHECKOUT_SOURCE];

export type CheckoutProduct = {
  id: string;
  name: string;
};

export type CheckoutEventProps = {
  seller_id?: string;
  seller_name?: string;
  products: CheckoutProduct[];
  source: CheckoutSource;
  planne_link_id?: string;
  planne_link_code?: string;
};

export const trackCheckoutStarted = (props: CheckoutEventProps, params: { isLoggedIn: boolean }) =>
  track(CHECKOUT_EVENT.checkoutStarted, { ...props, is_logged_in: params.isLoggedIn });

export const trackEmailSubmitted = (props: CheckoutEventProps) => track(CHECKOUT_EVENT.emailSubmitted, props);

export const trackAccountFound = (props: CheckoutEventProps) => track(CHECKOUT_EVENT.accountFound, props);

export const trackAccountNotFound = (props: CheckoutEventProps) => track(CHECKOUT_EVENT.accountNotFound, props);

export const trackLoginPasswordSelected = (props: CheckoutEventProps) =>
  track(CHECKOUT_EVENT.loginPasswordSelected, props);

export const trackLoginCodeSelected = (props: CheckoutEventProps) => track(CHECKOUT_EVENT.loginCodeSelected, props);

export const trackCodeSent = (props: CheckoutEventProps) => track(CHECKOUT_EVENT.codeSent, props);

export const trackCodeValidated = (props: CheckoutEventProps) => track(CHECKOUT_EVENT.codeValidated, props);

export const trackLoginCompleted = (props: CheckoutEventProps, method: LoginMode) =>
  track(CHECKOUT_EVENT.loginCompleted, { ...props, method });

export const trackAccountCreated = (props: CheckoutEventProps) => track(CHECKOUT_EVENT.accountCreated, props);

export const trackPaymentMethodSelected = (props: CheckoutEventProps, method: PaymentMethodCode) =>
  track(CHECKOUT_EVENT.paymentMethodSelected, { ...props, method });

export const trackPixQrShown = (props: CheckoutEventProps) => track(CHECKOUT_EVENT.pixQrShown, props);

export const trackThreeDsShown = (props: CheckoutEventProps, attemptNumber: number) =>
  track(CHECKOUT_EVENT.threeDsShown, { ...props, attempt_number: attemptNumber });

export const trackPaymentFailed = (
  props: CheckoutEventProps,
  params: { reason: string; attemptNumber: number; method?: PaymentMethodCode },
) =>
  track(CHECKOUT_EVENT.paymentFailed, {
    ...props,
    reason: params.reason,
    attempt_number: params.attemptNumber,
    method: params.method,
  });

export const trackCheckoutCompleted = (
  props: CheckoutEventProps,
  params: { method?: string; totalPriceCents: number },
) =>
  track(CHECKOUT_EVENT.checkoutCompleted, {
    ...props,
    method: params.method,
    total_price_cents: params.totalPriceCents,
  });
